import React, { useEffect, useState } from 'react';
import PublicTopNav from '../components/PublicTopNav';
import SiteFooter from '../components/SiteFooter';
import { COMPANY, CONTACT_PATH } from '../legal/company';
import usePageMeta from '../hooks/usePageMeta';

type Topic = 'support' | 'billing' | 'legal' | 'sales' | 'other';

const TOPICS: { id: Topic; label: string }[] = [
  { id: 'support', label: 'Product support' },
  { id: 'billing', label: 'Billing' },
  { id: 'legal', label: 'Legal or privacy' },
  { id: 'sales', label: 'Sales or custom plan (website, ERP, CRM)' },
  { id: 'other', label: 'Something else' },
];

const emailForTopic = (topic: Topic) =>
  topic === 'legal' ? COMPANY.legalEmail : COMPANY.supportEmail;

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState<Topic>('support');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [openedMail, setOpenedMail] = useState(false);

  usePageMeta({
    title: 'Contact OnTheWeb | Website Help & Business Enquiries',
    description:
      'Have questions before creating your website? Talk to OnTheWeb about features, plans or custom business needs. Already a user? Get help with your account.',
    path: CONTACT_PATH,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setOpenedMail(false);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please add your name, email, and a short message.');
      return;
    }

    const to = emailForTopic(topic);
    const topicLabel = TOPICS.find((t) => t.id === topic)?.label ?? 'Enquiry';
    const subject = `${COMPANY.brand}: ${topicLabel}`;
    const body = [
      `Name: ${name.trim()}`,
      `Email: ${email.trim()}`,
      phone.trim() ? `Phone: ${phone.trim()}` : null,
      `Topic: ${topicLabel}`,
      '',
      message.trim(),
    ]
      .filter(Boolean)
      .join('\n');

    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setOpenedMail(true);
  };

  return (
    <div className="nova-page">
      <PublicTopNav activePath={CONTACT_PATH} />

      <main className="pt-[68px]">
        <div className="max-w-content mx-auto px-6 lg:px-12 py-16 sm:py-20">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-nova-text mb-3">Contact us</h1>
          <p className="text-nova-text2 leading-relaxed max-w-2xl mb-12">
            Questions about your website, billing, or the product — write or call. We aim to reply within two
            business days.
          </p>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-4">
              <a
                href={`mailto:${COMPANY.supportEmail}`}
                className="nova-card-hover block no-underline"
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-nova-sky mb-2">Support</p>
                <p className="font-display text-lg font-bold text-nova-text">{COMPANY.supportEmail}</p>
                <p className="text-sm text-nova-text2 mt-1">Accounts, websites, and billing</p>
              </a>

              <a href={`mailto:${COMPANY.legalEmail}`} className="nova-card-hover block no-underline">
                <p className="text-xs font-semibold uppercase tracking-widest text-nova-sky mb-2">Legal &amp; privacy</p>
                <p className="font-display text-lg font-bold text-nova-text">{COMPANY.legalEmail}</p>
                <p className="text-sm text-nova-text2 mt-1">Terms, data, and grievance requests</p>
              </a>

              <a href={COMPANY.phoneHref} className="nova-card-hover block no-underline">
                <p className="text-xs font-semibold uppercase tracking-widest text-nova-sky mb-2">Phone</p>
                <p className="font-display text-lg font-bold text-nova-text">{COMPANY.phone}</p>
                <p className="text-sm text-nova-text2 mt-1">India</p>
              </a>

              <div className="nova-card">
                <p className="text-xs font-semibold uppercase tracking-widest text-nova-sky mb-2">Grievance officer</p>
                <p className="font-display font-bold text-nova-text">{COMPANY.grievanceOfficer}</p>
                <p className="text-sm text-nova-text2 mt-2 leading-relaxed">
                  For complaints under applicable Indian IT and consumer rules, email{' '}
                  <a href={`mailto:${COMPANY.legalEmail}`} className="text-nova-sky hover:text-nova-sky2">
                    {COMPANY.legalEmail}
                  </a>
                  .
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="nova-card space-y-5">
              <h2 className="font-display text-xl font-bold text-nova-text">Send a message</h2>
              <p className="text-sm text-nova-text2">
                This opens your email app addressed to us. If nothing opens, write directly to{' '}
                <a href={`mailto:${COMPANY.supportEmail}`} className="text-nova-sky hover:text-nova-sky2">
                  {COMPANY.supportEmail}
                </a>
                .
              </p>

              <div>
                <label htmlFor="contact-name" className="nova-label">
                  Name
                </label>
                <input
                  id="contact-name"
                  className="nova-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="nova-label">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  className="nova-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label htmlFor="contact-phone" className="nova-label">
                  Phone <span className="font-normal text-nova-text3">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  className="nova-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                />
              </div>

              <div>
                <label htmlFor="contact-topic" className="nova-label">
                  Topic
                </label>
                <select
                  id="contact-topic"
                  className="nova-input"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value as Topic)}
                >
                  {TOPICS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="nova-label">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  className="nova-input min-h-[140px] resize-y"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              {error && <p className="nova-alert-error">{error}</p>}
              {openedMail && (
                <p className="nova-alert-success">
                  Your email app should open with the message ready to send. If it does not, email{' '}
                  {emailForTopic(topic)} instead.
                </p>
              )}

              <button type="submit" className="btn-primary w-full justify-center">
                Open email to send
              </button>
            </form>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;
